import toast from 'react-hot-toast';
import i18n from '@/i18n';
import { healthCheckService } from '@/services/healthCheck.service';
import { ErrorPhaseManager } from '@/services/errorPhase.manager';

const CONNECTING_TOAST_ID = 'error-coordinator-connecting';
const ERROR_TOAST_ID = 'error-coordinator-error';

class ErrorCoordinator {
  private sources = new Map<string, ErrorPhaseManager>();
  private healthMonitorActive = false;
  private pendingHealthCheck = false;

  handleServerError(error: unknown, sourceKey: string): void {
    if (this.healthMonitorActive) return;
    if (this.isAuthError(error)) return;

    if (!this.sources.has(sourceKey)) {
      const manager = new ErrorPhaseManager({
        phase1Duration: 5_000,
        phase2Duration: 10_000,
        onPhaseChange: (phase) => {
          if (this.healthMonitorActive) return;
          if (phase === 'warning') {
            toast.loading(i18n.t('errors.connecting'), { id: CONNECTING_TOAST_ID });
          } else if (phase === 'error') {
            toast.dismiss(CONNECTING_TOAST_ID);
            toast.error(i18n.t('errors.serverUnreachable'), {
              id: ERROR_TOAST_ID,
              duration: Infinity,
            });
          }
        },
      });
      this.sources.set(sourceKey, manager);
      manager.start();
    }

    this.triggerHealthCheck();
  }

  handleRecovery(sourceKey: string): void {
    const manager = this.sources.get(sourceKey);
    if (!manager) return;
    manager.reset();
    this.sources.delete(sourceKey);

    if (this.sources.size === 0) {
      toast.dismiss(CONNECTING_TOAST_ID);
      toast.dismiss(ERROR_TOAST_ID);
    }
  }

  setHealthMonitorActive(active: boolean): void {
    this.healthMonitorActive = active;
    if (active) {
      // HealthMonitor is showing full-page UI — suppress all toasts and clear sources
      toast.dismiss(CONNECTING_TOAST_ID);
      toast.dismiss(ERROR_TOAST_ID);
      for (const [key, manager] of this.sources) {
        manager.reset();
        this.sources.delete(key);
      }
    }
  }

  private async triggerHealthCheck(): Promise<void> {
    if (this.pendingHealthCheck) return;
    this.pendingHealthCheck = true;

    try {
      const result = await healthCheckService.checkHealth();
      if (result.status !== 'healthy') {
        // Server is down — HealthMonitor will take over via its subscription
        // No action needed here; it will call setHealthMonitorActive(true)
      }
    } finally {
      this.pendingHealthCheck = false;
    }
  }

  private isAuthError(error: unknown): boolean {
    if (error && typeof error === 'object' && 'response' in error) {
      const status = (error as { response?: { status?: number } }).response?.status;
      return status === 401 || status === 403;
    }
    return false;
  }
}

export const errorCoordinator = new ErrorCoordinator();
